import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import colors from 'src/utils/colors';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { ProfileCard } from '../Settings/Profile/ProfileSummaryCard';
import { useGetProfiles } from '../Settings/hooks/useProfiles';
import { Grid, Divider, Skeleton } from '@mui/material';
import { SearchTextField } from 'src/components/BeaconV2Components/TableSearch';
import { z } from 'zod';
import { CreateProjectRequestSchema } from '../api/apiTypes';
import { StyledSettingInput } from '../Settings/components/StyledSettingInput';
import { styled } from '@mui/material/styles';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import { useSnackbar } from 'src/utils/Hooks';
import { useBeaconClientId } from 'src/utils/Hooks/reduxSelectors';
import { useQuery } from '@tanstack/react-query';
import { getQueryParamValue } from 'src/utils/app';
import { useEffect } from 'react';
import { useProjects } from './hooks/useProjects';
import { apiGetProjects } from '../api/contentGenApis';
import { useProjectsSubscription } from './hooks';

const ProjectSettingsFormSchema = CreateProjectRequestSchema.extend({
  profileId: z.string().nonempty('Please select a content profile')
});

type ProjectSettingsForm = z.infer<typeof ProjectSettingsFormSchema>;

// Styled divider to match ProfileForm
const StyledDivider = styled(Divider)({
  margin: '32px 0'
});

const ITEMS_PER_PAGE = 15;

export const ProjectSettings: React.FC<{ isExisting?: boolean }> = ({ isExisting = false }) => {
  useProjectsSubscription();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const { showSnackbar } = useSnackbar();
  const beaconClientId = useBeaconClientId();

  // Use projects hook for state management, navigation, and mutations
  const {
    projectId,
    projectName: storedProjectName,
    profileId: storedProfileId,
    setProjectDetails,
    cancelProjectFlow,
    navigateToProjectProducts,
    navigateToProjectReview,
    updateProjectMutation
  } = useProjects({ syncUrlParams: true });

  const profileIdFromUrl = getQueryParamValue('profileId');

  const { data: existingProjectData, isLoading: isLoadingProject } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const response = await apiGetProjects({ pageSize: 1000 });
      // Filter for the specific project
      const project = response.data.records.find((p) => p.projectId === projectId);
      return project || null;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always'
  });

  // Extract mutation properties
  const { mutateAsync: updateProjectAsync, isPending: isUpdatingProject } = updateProjectMutation;

  const methods = useForm<ProjectSettingsForm>({
    mode: 'all',
    resolver: zodResolver(ProjectSettingsFormSchema),
    defaultValues: {
      name: storedProjectName || '',
      profileId: storedProfileId || ''
    }
  });

  const {
    formState: { errors },
    watch,
    setValue,
    reset
  } = methods;
  const selectedProfileId = watch('profileId');
  const currentName = watch('name');

  // Enable Next button only when both name and profile are selected
  const isFormValid = !!(currentName && currentName.trim() && selectedProfileId);

  // Load existing project data into form when in existing mode
  useEffect(() => {
    if (isExisting && existingProjectData && !isLoadingProject) {
      reset({
        name: existingProjectData.name || '',
        profileId: existingProjectData.profileId || ''
      });

      // Also update the store with the existing project details
      setProjectDetails({
        projectId: existingProjectData.projectId,
        projectName: existingProjectData.name,
        profileId: existingProjectData.profileId || '',
        profileName: existingProjectData.profileName || '',
        profileVersion: existingProjectData.profileVersion || 0
      });
    }
  }, [isExisting, existingProjectData, isLoadingProject, reset, setProjectDetails]);

  // Reflect store-updated project name into the form when not actively edited
  useEffect(() => {
    if (isExisting && storedProjectName && currentName !== storedProjectName) {
      // Only update if the user hasn't modified the form
      if (!methods.formState.isDirty) {
        setValue('name', storedProjectName, { shouldDirty: false });
      }
    }
  }, [isExisting, storedProjectName, currentName, setValue, methods.formState.isDirty]);

  useEffect(() => {
    if (!isExisting && profileIdFromUrl && !selectedProfileId) {
      setValue('profileId', profileIdFromUrl, { shouldValidate: true });
    }
  }, [isExisting, profileIdFromUrl, selectedProfileId, setValue]);

  // Fetch active profiles
  const { data: profilesData, isLoadingProfiles } = useGetProfiles('ACTIVE');

  const handleCancel = () => {
    // Clear project flow state and navigate to projects list
    cancelProjectFlow();
  };

  const handleNext = async () => {
    // Validate current form
    const isValid = await methods.trigger(['name', 'profileId']);

    if (!isValid) {
      return;
    }

    try {
      // Get form values
      const formValues = methods.getValues();

      // Get selected profile details
      const selectedProfile = profilesData?.data?.find((p) => p.profileId === formValues.profileId);

      if (isExisting && projectId) {
        // Update existing project
        if (!beaconClientId) {
          showSnackbar({
            type: 'error',
            message: 'Beacon client ID not found.'
          });
          return;
        }

        await updateProjectAsync({
          projectId,
          beaconClientId,
          name: formValues.name,
          profileId: selectedProfile.profileId,
          profileVersion: selectedProfile.version,
          profileName: selectedProfile.profileName
        });

        // Update the store with new details
        setProjectDetails({
          projectId,
          projectName: formValues.name,
          profileId: selectedProfile.profileId,
          profileName: selectedProfile.profileName,
          profileVersion: selectedProfile.version
        });

        // For edit flow, navigate back to review page
        navigateToProjectReview('edit');
      } else {
        setProjectDetails({
          projectId: null,
          projectName: formValues.name,
          profileId: selectedProfile.profileId,
          profileName: selectedProfile.profileName,
          profileVersion: selectedProfile.version
        });

        // Navigate to products selection page
        navigateToProjectProducts(undefined, formValues.profileId);
      }
    } catch (error) {
      // Error handling is done in the mutation onError callback
      console.error('Error in handleNext:', error);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  // Filter profiles based on search and sort by updatedAt date (newest first)
  const filteredProfiles = profilesData?.data
    ?.filter((profile) =>
      profile.profileName?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    ?.sort((a, b) => {
      if (!a.updatedAt) return 1;
      if (!b.updatedAt) return -1;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

  // Pagination logic
  const totalProfiles = filteredProfiles?.length || 0;
  const totalPages = Math.ceil(totalProfiles / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedProfiles = filteredProfiles?.slice(startIndex, endIndex);

  return (
    <FormProvider {...methods}>
      <Flex flexDirection="column">
        {/* Header */}
        <Flex flexDirection="column" gap={8} sx={{ marginTop: '32px' }}>
          <Text variant="h4">Project Details</Text>
          <Text variant="body1">
            Name your project below.
          </Text>
        </Flex>

        {/* Project Name Input */}
        <Flex flexDirection="column" sx={{ marginTop: '32px' }}>
          <StyledSettingInput<ProjectSettingsForm>
            label="Project Name"
            placeholder="Name your project"
            name="name"
            characterLimit={100}
            defaultValue=""
            filterRegex={/a-zA-Z0-9 /}
          />
        </Flex>

        <StyledDivider />

        {/* Assign Content Profile Section */}
        <Flex flexDirection="column">
          <Text sx={{ fontWeight: 700, fontSize: '20px', letterSpacing: '0.08px', lineHeight: '24px' }}>
            Assign a Content Profile
          </Text>
          <Text variant="body1" sx={{ marginTop: '8px' }}>
            Choose the content profile you would like to assign to this project.
          </Text>

          {/* Search */}
          <Flex sx={{ marginTop: '32px' }}>
            <SearchTextField placeholder="Search profiles" value={searchQuery} onChange={handleSearchChange} />
          </Flex>

          {/* Profile Cards Grid */}
          <Grid container spacing={'16px'} sx={{ marginTop: '16px', alignContent: 'flex-start' }}>
            {isLoadingProfiles || (isExisting && isLoadingProject) ? (
              <>
                {[...Array(6)].map((_, index) => (
                  <Grid item xs={4} key={index}>
                    <Flex
                      flexDirection="column"
                      sx={{
                        border: `1px solid ${colors.graysSecondary}`,
                        borderRadius: '8px',
                        padding: '16px',
                        width: '336px',
                        height: '153px',
                        backgroundColor: 'white'
                      }}
                    >
                      {/* Profile name skeleton */}
                      <Skeleton variant="text" width="80%" height={24} sx={{ marginBottom: '4px' }} />
                      <Skeleton variant="text" width="60%" height={24} />
                      
                      {/* Divider */}
                      <Divider sx={{ marginTop: 'auto', marginBottom: '16px' }} />
                      
                      {/* Bottom section with avatar and text */}
                      <Flex justifyContent="space-between" alignItems="center">
                        <Flex alignItems="center">
                          {/* Avatar skeleton */}
                          <Skeleton
                            variant="circular"
                            width={30}
                            height={30}
                            sx={{ marginRight: '8px', flexShrink: 0 }}
                          />
                          {/* Text skeletons */}
                          <Flex flexDirection="column">
                            <Skeleton variant="text" width={100} height={16} sx={{ marginBottom: '2px' }} />
                            <Skeleton variant="text" width={120} height={14} />
                          </Flex>
                        </Flex>
                        {/* Select button skeleton */}
                        <Skeleton variant="rounded" width={74} height={24} sx={{ borderRadius: '16px' }} />
                      </Flex>
                    </Flex>
                  </Grid>
                ))}
              </>
            ) : paginatedProfiles && paginatedProfiles.length > 0 ? (
              paginatedProfiles.map((profile) => (
                <Grid item xs={4} key={profile.profileId}>
                  <ProfileCard
                    profile={profile}
                    isSelectable
                    isSelected={selectedProfileId === profile.profileId}
                    onSelect={(profileId) => {
                      if (selectedProfileId === profileId) {
                        setValue('profileId', '', { shouldValidate: true });
                      } else {
                        setValue('profileId', profileId, { shouldValidate: true });
                      }
                    }}
                  />
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Text>No profiles found.</Text>
              </Grid>
            )}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Flex justifyContent="flex-end" sx={{ marginTop: '24px' }}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onClickNext={() => {
                  if (currentPage < totalPages) {
                    setCurrentPage(currentPage + 1);
                  }
                }}
                onClickPrevious={() => {
                  if (currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                  }
                }}
                disableNextButton={currentPage >= totalPages}
              />
            </Flex>
          )}
        </Flex>

        {/* Footer Buttons */}
        <Flex justifyContent="space-between" sx={{ marginTop: '64px', marginBottom: '48px' }}>
          <SlButton
            onClick={handleCancel}
            buttonSx={{
              border: `1px solid ${colors.graysSecondary}`
            }}
          >
            Cancel
          </SlButton>

          <SlButton
            variant="contained"
            onClick={handleNext}
            disabled={!isFormValid || isUpdatingProject || (isExisting && isLoadingProject)}
          >
            {isUpdatingProject ? 'Saving...' : isExisting ? 'Save' : 'Next'}
          </SlButton>
        </Flex>
      </Flex>
    </FormProvider>
  );
};
